import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { StoreContext } from '@/components/providers/store.provider';
import type { AccountResponse, LoginRequest, LoginResponse, RefreshResponse } from './types';
import { useApi } from '../api/service';
import type { ResponseType } from '@/lib/types/response.type';
import { errorHandler } from '@/lib/utils/error-handler';

export const useLogin = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const login = useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: async (dto: LoginRequest) => {
      try {
        const response = await api.post<LoginResponse>('/auth/login', {
          message: dto.message.trim(),
          signature: dto.signature.trim(),
          address: dto.address.trim(),
        });
        localStorage.setItem('access-token', response.data.result.token);
        userStore.setAuth(true);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { login };
};

export const useAccount = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const account = useQuery<AccountResponse, Error>({
    queryKey: ['/auth/account', userStore.auth],
    queryFn: async () => {
      try {
        const response = await api.get<AccountResponse>('/auth/account');
        userStore.set(response.data.result);
        userStore.setAuth(true);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
    enabled: userStore.auth,
    refetchInterval: 60000,
    staleTime: 10000,
    refetchOnWindowFocus: true,
  });
  return { account };
};

export const useLogout = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const logout = useMutation<ResponseType, Error>({
    mutationFn: async () => {
      try {
        const response = await api.delete<ResponseType>('/auth/logout');
        userStore.setAuth(false);
        userStore.delete();
        localStorage.removeItem('access-token');
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { logout };
};

export const useRefresh = () => {
  const { api } = useApi();
  const { userStore } = useContext(StoreContext);
  const refresh = useMutation<RefreshResponse, Error>({
    mutationFn: async () => {
      try {
        const response = await api.get<RefreshResponse>('/auth/refresh');
        localStorage.setItem('access-token', response.data.result);
        userStore.setAuth(true);
        return response.data;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { refresh };
};
